import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Design system
import type { SelectOptionPropsType } from '@Shelly/shelly-design-system/index';
//Helpers
import { devices_settings } from 'src/stores/device-runtime/status';
import { capFirstChar } from 'src/helpers/utilities';
import {
	getCameraZone,
	getCameraZonesList,
	getZoneId,
} from 'src/components/Camera/helpers/camera-zone-helpers';
import { supportsCameraSetRpc } from 'src/components/Camera/helpers/camera-features';
import { isCameraSoundName } from 'src/components/Camera/helpers/camera-sounds';
//Types
import type { Device } from 'src/types/Device';
import type { ActionJob, LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';
import type { CameraZoneType } from 'src/components/Camera/types/camera-zones';

export type CameraLocalAction = 'capture' | 'record' | 'sound' | 'privacy' | 'arm' | 'zone';
export type CameraLocalActionZone = {
	id: string;
	title: string;
	type: CameraZoneType;
	icon: string;
};

export const CAMERA_DURATION_MIN = 1;
export const CAMERA_DURATION_MAX = 3600;
export const CAMERA_DURATION_DEFAULT = 30;

export const CAMERA_SET_METHOD = 'camera.set';
export const CAMERA_PLAY_SOUND_METHOD = 'camera.playsound';

/**
 * @deprecated LEGACY_CAMERA_ARM_PRIVACY - superseded by `camera.set`, which takes `arm` and
 * `privacy` at the top level. Not to be confused with `camerazone.setconfig`, which is a
 * different component and is unaffected.
 */
export const CAMERA_LEGACY_SET_CONFIG_METHOD = 'camera.setconfig';

export const CAMERA_ZONE_SET_CONFIG_METHOD = 'camerazone.setconfig';

export const CAMERA_LOCAL_ACTION_METHODS: Record<CameraLocalAction, string> = {
	record: 'camera.startrecording',
	capture: 'camera.captureimage',
	sound: CAMERA_PLAY_SOUND_METHOD,
	privacy: CAMERA_SET_METHOD,
	arm: CAMERA_SET_METHOD,
	zone: CAMERA_ZONE_SET_CONFIG_METHOD,
};

/**
 * The RPC that sets `arm` / `privacy` on this device. Older firmware keeps them in the
 * camera config and only answers `camera.setconfig`.
 *
 * @deprecated LEGACY_CAMERA_ARM_PRIVACY - collapses back to `CAMERA_SET_METHOD` once no
 * supported firmware keeps these in config.
 */
export const getCameraSetMethod = (device: Device): string =>
	supportsCameraSetRpc(device) ? CAMERA_SET_METHOD : CAMERA_LEGACY_SET_CONFIG_METHOD;

/** Whether the method sets `arm` / `privacy`, under either the current or the legacy name. */
export const isCameraArmPrivacyMethod = (method: string): boolean =>
	method === CAMERA_SET_METHOD || method === CAMERA_LEGACY_SET_CONFIG_METHOD;

/**
 * Map a saved action's method onto the variant this device supports, so an action stored
 * under the other one still resolves to a control option instead of silently falling back
 * to the first camera action.
 *
 * @deprecated LEGACY_CAMERA_ARM_PRIVACY - nothing to normalise once only `camera.set` exists.
 */
export const normalizeCameraLocalActionMethod = (method: string, device: Device): string => {
	const normalized = (method || '').split(':')[0].toLowerCase();
	return isCameraArmPrivacyMethod(normalized) ? getCameraSetMethod(device) : normalized;
};

/**
 * Read `arm` / `privacy` off an action regardless of which method saved it: `camera.set`
 * carries them at the top level, `camera.setconfig` nests them under `config`.
 */
export const getCameraActionFlags = (action?: ActionJob): { arm?: boolean; privacy?: boolean } => ({
	// LEGACY_CAMERA_ARM_PRIVACY: drop the `config` fallbacks once no saved action uses them.
	arm: action?.params?.arm ?? action?.params?.config?.arm,
	privacy: action?.params?.privacy ?? action?.params?.config?.privacy,
});

export const getCameraLocalActionCards = (showZone = false) => {
	const $format = get(format);
	const cards = [
		{
			id: 'record' as CameraLocalAction,
			title: $format('local_action_camera_start_recording', { default: 'Start Recording' }),
			icon: 'fas fa-circle-dot',
		},
		{
			id: 'capture' as CameraLocalAction,
			title: $format('local_action_camera_capture_image', { default: 'Capture Image' }),
			icon: 'fas fa-camera',
		},
		{
			id: 'sound' as CameraLocalAction,
			title: $format('scene_camera_action_play_sound', { default: 'Play sound' }),
			icon: 'fas fa-volume-high',
		},
		{
			id: 'privacy' as CameraLocalAction,
			title: $format('camera_privacy_mode', { default: 'Privacy Mode' }),
			icon: 'fas fa-video-slash',
		},
		{
			id: 'arm' as CameraLocalAction,
			title: $format('camera_arm_disarm', { default: 'Arm / Disarm' }),
			icon: 'fas fa-shield',
		},
	];

	if (showZone) {
		cards.push({
			id: 'zone' as CameraLocalAction,
			title: $format('scene_camera_action_zone', { default: 'Camera zone' }),
			icon: 'fas fa-vector-square',
		});
	}

	return cards;
};

export const getCameraStateCards = (method: CameraLocalAction) => {
	const $format = get(format);
	switch (method) {
		case 'privacy':
			return [
				{
					value: true,
					title: $format('camera_privacy_mode_on', { default: 'Privacy On' }),
					icon: 'fas fa-video-slash',
				},
				{
					value: false,
					title: $format('camera_privacy_mode_off', { default: 'Privacy Off' }),
					icon: 'fas fa-video',
				},
			];
		case 'arm':
			return [
				{
					value: true,
					title: $format('camera_motion_detection_armed', { default: 'Armed' }),
					icon: 'fas fa-shield',
				},
				{
					value: false,
					title: $format('camera_motion_detection_disarmed', { default: 'Disarmed' }),
					icon: 'fas fa-shield-halved',
				},
			];
		case 'zone':
			return [
				{
					value: true,
					title: $format('camera_zone_enable_action', { default: 'Enable zone' }),
					icon: 'fas fa-circle-check',
				},
				{
					value: false,
					title: $format('camera_zone_disable_action', { default: 'Disable zone' }),
					icon: 'fas fa-circle-xmark',
				},
			];
		default:
			return [];
	}
};

/** Whether the action sets `arm` or `privacy`, as opposed to recording, capture or zone. */
export const isCameraSetAction = (action: CameraLocalAction): boolean =>
	action === 'arm' || action === 'privacy';

export const getCameraConfigActionTitle = (action: CameraLocalAction): string => {
	const $format = get(format);

	switch (action) {
		case 'privacy':
			return $format('scene_camera_select_privacy', { default: 'Set privacy mode' });
		case 'arm':
			return $format('scene_camera_select_arm', { default: 'Arm or disarm camera' });
		default:
			return '';
	}
};

export const getSelectedCameraAction = (method: string): CameraLocalAction => {
	// LEGACY_CAMERA_ARM_PRIVACY: `camera.setconfig` resolves to the same cards as `camera.set`.
	const normalizedMethod = (method || '').split(':')[0].toLowerCase();
	const normalized =
		normalizedMethod === CAMERA_LEGACY_SET_CONFIG_METHOD ? CAMERA_SET_METHOD : normalizedMethod;
	return (Object.entries(CAMERA_LOCAL_ACTION_METHODS) as [CameraLocalAction, string][]).find(
		([, value]) => value === normalized,
	)?.[0];
};

export const isCameraLocalActionMethod = (method: string): boolean => {
	// LEGACY_CAMERA_ARM_PRIVACY: `camera.setconfig` stays listed so already-saved actions keep
	// their rich previews instead of degrading to a raw method string.
	return /^(camera\.(captureimage|startrecording|playsound|set|setconfig)|camerazone\.setconfig)$/i.test(
		method?.split(':')[0] || '',
	);
};

const getCameraZoneTypeIcon = (type: CameraZoneType): string =>
	type === 'privacy' ? 'fas fa-video-slash' : 'fas fa-vector-square';

export const getCameraLocalActionZones = (device: Device): CameraLocalActionZone[] => {
	const deviceSettings = devices_settings.getByRealId(device.id);
	const $format = get(format);
	if (!deviceSettings) {
		return [];
	}

	return getCameraZonesList(deviceSettings)
		.map((componentKey: string) => {
			const zoneId = getZoneId(componentKey);
			const zone = getCameraZone(deviceSettings, zoneId);
			const type = zone?.type ?? 'motion';
			return {
				id: String(zoneId),
				title:
					zone?.name ||
					$format('camera_zone', {
						values: { id: zoneId },
						default: `Zone ${zoneId}`,
					}),
				type,
				icon: getCameraZoneTypeIcon(type),
			};
		})
		.sort((a, b) => Number(a.id) - Number(b.id));
};

/**
 * The arm/privacy option, hydrated from either payload shape and emitted in the shape this
 * device's firmware expects.
 */
const getCameraSetLocalAction = (
	action: ActionJob,
	device: Device,
	params: { id: number },
): LocalControlOptions => {
	const $format = get(format);
	const target = getTargetOptions(device);
	const { arm, privacy } = getCameraActionFlags(action);
	const title = $format('local_action_camera_configure', { default: 'Configure Camera' });
	const method = getCameraSetMethod(device);

	if (method === CAMERA_LEGACY_SET_CONFIG_METHOD) {
		// LEGACY_CAMERA_ARM_PRIVACY: remove this branch with the old firmware.
		return {
			title,
			value: method,
			attr: { target, config: { arm: arm != null, privacy: privacy != null } },
			params: { ...params, config: { arm: arm || false, privacy: privacy || false } },
		};
	}

	return {
		title,
		value: method,
		attr: { target, arm: arm != null, privacy: privacy != null },
		params: { ...params, arm: arm || false, privacy: privacy || false },
	};
};

export const getCameraLocalActions = (
	action: ActionJob,
	device: Device,
	params: { id: number },
): LocalControlOptions[] => {
	const $format = get(format);
	const target = getTargetOptions(device);
	const zones = getCameraLocalActionZones(device);
	const method = (action.method || '').split(':')[0].toLowerCase();
	const isZoneAction = method === CAMERA_ZONE_SET_CONFIG_METHOD;
	const isSoundAction = method === CAMERA_PLAY_SOUND_METHOD;
	const selectedSound =
		isSoundAction && isCameraSoundName(action.params?.sound) ? action.params.sound : null;

	const actions: LocalControlOptions[] = [
		{
			title: $format('local_action_camera_start_recording', {
				default: 'Start Recording',
			}),
			value: 'camera.startrecording',
			attr: {
				target,
				duration: action.params?.duration != null,
			},
			params: {
				...params,
				duration: action.params?.duration ?? null,
			},
		},
		{
			title: $format('local_action_camera_capture_image', {
				default: 'Capture Image',
			}),
			value: 'camera.captureimage',
			attr: { target },
			params,
		},
		{
			title: $format('scene_camera_action_play_sound', { default: 'Play sound' }),
			value: CAMERA_PLAY_SOUND_METHOD,
			attr: {
				target,
				sound: Boolean(selectedSound),
			},
			params: {
				...params,
				sound: selectedSound,
			},
		},
		getCameraSetLocalAction(action, device, params),
	];

	if (zones.length > 0) {
		actions.push({
			title: $format('scene_camera_action_zone', {
				default: 'Camera zone',
			}),
			value: CAMERA_ZONE_SET_CONFIG_METHOD,
			attr: {
				config: {
					enable: isZoneAction && action.params?.config?.enable != null,
				},
			},
			params: {
				...(isZoneAction && action.params?.id != null ? { id: action.params.id } : {}),
				config: {
					enable: action.params?.config?.enable ?? false,
				},
			},
		});
	}

	return actions;
};

const getTargetOptions = (device: Device): SelectOptionPropsType[] => {
	const $format = get(format);
	const channelsOptions: SelectOptionPropsType[] = [];
	const deviceSettings = devices_settings.getByRealId(device.id) || {};
	Object.keys(deviceSettings).forEach((key) => {
		if (key.startsWith('camera:')) {
			const cid = deviceSettings[key]?.id ?? 0;
			channelsOptions.push({
				title:
					deviceSettings[key]?.name ||
					$format('webhooks_component_camera', {
						values: { id: cid },
						default: `Camera (${cid})`,
					}),
				value: cid,
			});
		}
	});
	return channelsOptions;
};

export const getCameraComponents = (deviceSetting: any) => {
	const options = [];
	const components = Object.keys(deviceSetting || {})
		.filter((i) => /^(camera|camerazone):\d/.test(i))
		.sort();
	const cameraCount = components.filter((component) => component.startsWith('camera:')).length;
	for (let i = 0; i < components.length; ++i) {
		const type = components[i].split(':')[0];
		const component = deviceSetting[components[i]];
		const cid = component.id ?? Number(components[i].split(':')[1]) ?? 0;
		const showId = type !== 'camera' || cameraCount > 1;
		const title = component.name || `${type}${showId ? ` (${cid})` : ''}`;
		options.push({
			title: capFirstChar(title),
			type,
			component: components[i],
			inputType: type,
			cid,
		});
	}
	return options;
};
